<template>
  <aside
    id="componentTableOfContents"
    v-scroll-to-item
    v-trap-focus
    :aria-label="t('tableOfContents.componentTitle')"
    class="table-contents flex absolute h-auto max-w-md"
    @keydown.esc="close()"
  >
    <button
      id="closeTableOfContents"
      :title="t('reader.close')"
      :aria-label="t('reader.close') + ' ' + t('tableOfContents.title')"
      class="button close-sidebar absolute flex justify-center items-center cursor-pointer rounded-full"
      @click="close()"
    >
      <span class="material-icons" aria-hidden="true">close</span>
    </button>
    <h2 class="title absolute w-full flex justify-center items-center text-xl">
      {{ t("tableOfContents.title") }}
    </h2>
    <nav
      class="table-contents__container w-full pt-3 px-2 overflow-y-auto overscroll-contain"
      aria-label="Table of contents items"
    >
      <LinksList :items="navigator.tableOfContents" />
    </nav>
  </aside>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";

import { trapFocus } from "@/helpers/trapFocus";
import LinksList from "@/components/shared/LinksList.vue";

import { useNavigatorWithHistory } from "../navigator";
import { useUILayers } from "../UILayers";

const { t } = useI18n();

const navigator = useNavigatorWithHistory();

const { close } = useUILayers();

const vScrollToItem = {
  mounted: (el) => {
    const nav = el.getElementsByClassName("show-content-fade");
    const activeItem = el.getElementsByClassName("active")[0]; //get the active item in the list
    // Delay 300ms fade animations to not overlay with slide animations
    if (activeItem) {
      setTimeout(() => {
        Array.from(nav)?.forEach((list: HTMLElement) => {
          list.classList.add("show-nav");
        });
        activeItem.children[0]?.focus();
      }, 300);
    } else {
      Array.from(nav)?.forEach((list: HTMLElement) => {
        list.classList.add("show-nav");
      });
      setTimeout(() => {
        document.getElementById("closeTableOfContents")?.focus();
      }, 300);
    }
  },
  onBeforeMount: (el) => {
    const nav = el.getElementsByClassName("show-content-fade")[0];
    nav?.classList.remove("show-nav");
  },
};

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = document.getElementById("componentTableOfContents");

  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.table-contents {
  z-index: 3;
  height: var(--safe-height);
  background-color: var(--DM__theme-fill-color);
  color: var(--DM__theme-font-color);
  right: 0;
  width: 100%;
  .title {
    top: 0;
    height: 65px;
  }
  .close-sidebar {
    top: $spacingL - $spacingXS;
  }
  &__container {
    border-top: 65px solid var(--DM__theme-fill-color);
    border-bottom: $spacingM solid var(--DM__theme-fill-color);
    box-shadow: var(--DM__theme-shadow-color);
    @include scrollBar();
  }
  &:focus {
    outline: 2px solid var(--DM__theme-border-color);
    outline-offset: -2px;
  }
}
</style>
